import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { FormBase, FormModal } from '@gbs-monorepo-packages/common';
import {
  type MultipleDeleteSchema,
  multipleDeleteSchema,
} from '@gbs-monorepo/edge/formSchemas/pageSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  DialogModal,
  type IDialogModalProps,
} from '../../../../components/DialogModal';
import { type ICoursePageDTO } from '../../../../services/coursePages';
import {
  CustomCheckBoxGroup,
  CustomCheckbox,
  Flex,
  ModalText,
  SelectContainer,
  Title,
  TitleSelectAll,
} from './styles';

interface IDeletePageModalProps
  extends Partial<
    Omit<IDialogModalProps, 'children' | 'onAccept' | 'onOpenChange'>
  > {
  onAccept: (pageIds: number[]) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  coursePages: ICoursePageDTO[];
}

export const MultiplePagesDelete = ({
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  loading,
  coursePages,
  ...props
}: IDeletePageModalProps): JSX.Element | null => {
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const multipleDelete = useForm<MultipleDeleteSchema>({
    resolver: zodResolver(multipleDeleteSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      pages: [],
      selectedAll: false,
    },
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isValid },
    setValue,
  } = multipleDelete;

  const values = watch();

  const allPageIds = useMemo(
    () => coursePages.map((page) => page.id),
    [coursePages]
  );

  const isAllSelected = useMemo(() => {
    const selectedPages = values.pages ?? [];
    return (
      allPageIds.length > 0 &&
      allPageIds.every((id) => selectedPages.includes(id))
    );
  }, [allPageIds, values.pages]);

  useEffect(() => {
    setValue('selectedAll', isAllSelected, { shouldValidate: false });
  }, [isAllSelected, setValue]);

  const onSubmit: SubmitHandler<MultipleDeleteSchema> = useCallback(
    async (data: MultipleDeleteSchema) => {
      setLoadingDelete(true);
      setOpenConfirm(false);
      try {
        await onAccept?.(data.pages);
        reset();
        onDecline?.();
      } finally {
        setLoadingDelete(false);
      }
    },
    [onAccept, onDecline, reset]
  );

  const handleSelectPage = useCallback(
    (pageId: number) => {
      const currentPages = values.pages ?? [];
      const newPages = currentPages.includes(pageId)
        ? currentPages.filter((id: number) => id !== pageId)
        : [...currentPages, pageId];
      setValue('pages', newPages, { shouldValidate: true });
    },
    [setValue, values.pages]
  );

  const handleSelectAll = useCallback(() => {
    setValue('pages', allPageIds, { shouldValidate: true });
  }, [allPageIds, setValue]);

  const handleDeselectAll = useCallback(() => {
    setValue('pages', [], { shouldValidate: true });
  }, [setValue]);

  const handleDeclineDeletePage = useCallback(() => {
    reset();
    setOpenConfirm(false);
    onDecline?.();
  }, [onDecline, reset]);

  const handleConfirmDelete = useCallback(() => {
    setOpenConfirm((prev) => !prev);
  }, []);

  return !open ? null : (
    <>
      <FormBase.Provider {...multipleDelete}>
        <FormModal
          acceptText="Delete"
          dataCy="delete-course-multiple-page-dialog-modal"
          declineText="Cancel"
          mainText={`Select pages to delete`}
          {...props}
          loading={loadingDelete || loading}
          onAccept={() => {
            handleConfirmDelete();
          }}
          disabled={!isValid}
          onDecline={handleDeclineDeletePage}
          onOpenChange={handleDeclineDeletePage}
          open={!waitToOpen}
        >
          <FormBase.Content>
            <Flex>
              <CustomCheckbox
                id="selectAll"
                dataCy="checkbox-selectAll"
                value={isAllSelected ? 'true' : 'false'}
                onClick={isAllSelected ? handleDeselectAll : handleSelectAll}
              />
              <TitleSelectAll>Select all</TitleSelectAll>
            </Flex>

            <SelectContainer data-cy="select-delete-container">
              <CustomCheckBoxGroup
                type="multiple"
                data-cy="select-delete-container"
              >
                {coursePages.map((page, index) => (
                  <Flex key={page.id}>
                    <CustomCheckbox
                      value={page.id.toString()}
                      dataCy={`checkbox-card-${index}`}
                      onClick={() => {
                        handleSelectPage(page.id);
                      }}
                      checked={values.pages?.includes(page.id)}
                    />
                    <Title>{page.title}</Title>
                  </Flex>
                ))}
              </CustomCheckBoxGroup>
            </SelectContainer>
          </FormBase.Content>
        </FormModal>
      </FormBase.Provider>

      <DialogModal
        dataCy="delete-course-multiple-page-dialog-modal"
        acceptText="Confirm"
        declineText="Cancel"
        mainText={`Are you sure you want to delete?`}
        {...props}
        onAccept={() => {
          void handleSubmit(onSubmit)();
        }}
        loading={loadingDelete || loading}
        onDecline={handleConfirmDelete}
        onOpenChange={handleConfirmDelete}
        open={openConfirm}
      >
        <ModalText data-cy="text-informationDeleteMultiplePages">
          Existing pages in the database will be deleted.
        </ModalText>
      </DialogModal>
    </>
  );
};
